import {
  type Dispatch,
  type FC,
  type SetStateAction,
  useContext,
  useEffect,
} from 'react';
import { type RectReadOnly } from 'react-use-measure';
import { type PlantTypeText } from '@common/types/plant';
import { type ResourceTypeText } from '@common/types/resource';
import { ModalContext } from '@components/Modals/Modal/config';
import { calculateInstantCompleteValue } from '@shared-libs/index';

import { useClearFactoryProductionMutation } from '@/api/queries/factory';
import { useCountDownTimer } from '@/common/hooks/count-down-timer';
import { divide, multiplication } from '@/common/lib/decimal';
import AmountToProduce from '@/components/Plant/AmountToProduce/AmountToProduce';

import { ProductionAnimation } from '../../ProductionAnimation/ProductionAnimation';

interface ProductionInfoProps {
  plantId: string;
  source: ResourceTypeText;
  material: PlantTypeText;
  incommingStorage: number;
  productionRateOriginal: number;
  updatedAt: string;
  toAdd?: number;
  productionRate: number;
  producted: number;
  pullCoords: RectReadOnly | null;
  pullAnimating: boolean;
  setPullAnimating: Dispatch<SetStateAction<boolean>>;
  timeLeft?: number;
}

const ProductionInfo: FC<ProductionInfoProps> = ({
  plantId,
  source,
  material,
  incommingStorage,
  producted,
  productionRateOriginal,
  pullAnimating,
  pullCoords,
  setPullAnimating,
  timeLeft = 0,
  toAdd,
  productionRate,
}) => {
  const { setBooostProductionDynamicData, openModal } =
    useContext(ModalContext);
  const [clearFactoryProduction, clearFactoryProductionApiState] =
    useClearFactoryProductionMutation();
  const seconds = useCountDownTimer(timeLeft);

  useEffect(() => {
    const inPool = incommingStorage - producted;
    const hoursToFinishProduction = divide(inPool, productionRateOriginal);
    const secondsToFinishProdution = multiplication(
      hoursToFinishProduction,
      3600,
    );

    const price = calculateInstantCompleteValue(secondsToFinishProdution);
    setBooostProductionDynamicData({
      inPool,
      price,
    });
  }, [
    producted,
    incommingStorage,
    productionRateOriginal,
    setBooostProductionDynamicData,
  ]);

  const openBoostProduction = (): void => {
    if (seconds > 0) {
      openModal({
        renderKind: 'boostProduction',
        props: { material, plantId },
      });
    }
  };

  const clearProduction = (): void => {
    clearFactoryProduction({ id: plantId });
  };

  return (
    <div className="w-full flex flex-col items-center justify-center isolate relative z-20 mb-4">
      <div className="w-full flex items-center justify-center px-2 mb-2">
        <AmountToProduce
          cancelDisabled={
            clearFactoryProductionApiState.isLoading ||
            incommingStorage === 0 ||
            producted === incommingStorage
          }
          value={incommingStorage - producted}
          onBoost={openBoostProduction}
          onCancel={clearProduction}
        />
      </div>
      <ProductionAnimation
        material={material}
        resource={source}
        isInProgress={!!incommingStorage && incommingStorage > producted}
        pullAnimating={pullAnimating}
        setPullAnimating={setPullAnimating}
        pullCoords={pullCoords}
        toAdd={toAdd}
        productionRate={productionRate}
      />
    </div>
  );
};

export { ProductionInfo };
